import type {
  OnboardingLadderContext,
  ResolvedLadderStep,
  OnboardingLadder,
} from '../types/onboarding-ladder-types';
import { ONBOARDING_LADDER_STEP_KIND } from '@/features/setup/types/onboarding-ladder-step-enums';
import {
  OnboardingLadderStepId,
  OnboardingLadderStepState,
} from '../types/onboarding-ladder-enums';

interface OnboardingLadderStepDef {
  id: OnboardingLadderStepId;
  label: string;
  description: string;
  kind: ONBOARDING_LADDER_STEP_KIND;
  href: string;
  isComplete: (ctx: OnboardingLadderContext) => boolean;
}

export const ONBOARDING_LADDER_STEPS: readonly OnboardingLadderStepDef[] = [
  {
    id: OnboardingLadderStepId.CONNECT_SHOPIFY,
    label: 'Connect your Shopify store',
    description: 'Bring orders and customer data into Upstack.',
    kind: ONBOARDING_LADDER_STEP_KIND.DERIVED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.shopifyConnected || ctx.isShopifyAppCustomer,
  },
  {
    id: OnboardingLadderStepId.INSTALL_TRACKING,
    label: 'Turn on Shopify tracking',
    description: 'Theme app extension and checkout pixel.',
    kind: ONBOARDING_LADDER_STEP_KIND.ACKNOWLEDGED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.trackingInstalled,
  },
  {
    id: OnboardingLadderStepId.CONNECT_FACEBOOK_SOURCE,
    label: 'Connect Meta Ads',
    description: 'Import ad spend data for reporting.',
    kind: ONBOARDING_LADDER_STEP_KIND.DERIVED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.facebookSourceConnected,
  },
  {
    id: OnboardingLadderStepId.ENABLE_FACEBOOK_DESTINATION,
    label: 'Set up Meta destination',
    description: 'Send server-side conversions to Meta.',
    kind: ONBOARDING_LADDER_STEP_KIND.DERIVED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.facebookDestinationEnabled,
  },
  {
    id: OnboardingLadderStepId.SHOPIFY_DATA_SHARING_OFF,
    label: 'Turn off Shopify sales-channel data sharing',
    description: 'Avoid duplicate events to Meta.',
    kind: ONBOARDING_LADDER_STEP_KIND.ACKNOWLEDGED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.shopifyDataSharingDisabled,
  },
  {
    id: OnboardingLadderStepId.CONNECT_KLAVIYO,
    label: 'Connect Klaviyo',
    description: 'Send store activity to Klaviyo.',
    kind: ONBOARDING_LADDER_STEP_KIND.DERIVED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.klaviyoConnected,
  },
  {
    id: OnboardingLadderStepId.KLAVIYO_FLOWS,
    label: 'Set up Klaviyo flows and invites',
    description: 'Abandonment flows and team access.',
    kind: ONBOARDING_LADDER_STEP_KIND.ACKNOWLEDGED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.klaviyoFlowsConfigured,
  },
  {
    id: OnboardingLadderStepId.CONNECT_GOOGLE_SOURCE,
    label: 'Connect Google data source',
    description: 'Import Google ad spend for reporting.',
    kind: ONBOARDING_LADDER_STEP_KIND.DERIVED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.googleSourceConnected,
  },
  {
    id: OnboardingLadderStepId.ENABLE_GOOGLE_DESTINATION,
    label: 'Set up Google destination',
    description: 'Send server-side conversions to Google.',
    kind: ONBOARDING_LADDER_STEP_KIND.DERIVED,
    href: '/get-started',
    isComplete: (ctx) => ctx.signals.googleDestinationEnabled,
  },
];

function resolveState(
  completed: boolean,
  isComingSoon: boolean,
  isFirstIncomplete: boolean
): OnboardingLadderStepState {
  if (isComingSoon) return OnboardingLadderStepState.COMING_SOON;
  if (completed) return OnboardingLadderStepState.COMPLETE;
  if (isFirstIncomplete) return OnboardingLadderStepState.CURRENT;
  return OnboardingLadderStepState.UPCOMING;
}

export function resolveLadder(ctx: OnboardingLadderContext): OnboardingLadder {
  let firstIncompleteAssigned = false;
  const steps: ResolvedLadderStep[] = ONBOARDING_LADDER_STEPS.map((step) => {
    const isComingSoon = step.kind === 'coming_soon';
    const completed = !isComingSoon && step.isComplete(ctx);
    const isFirstIncomplete = !completed && !isComingSoon && !firstIncompleteAssigned;
    if (isFirstIncomplete) {
      firstIncompleteAssigned = true;
    }
    return {
      id: step.id,
      label: step.label,
      description: step.description,
      kind: step.kind,
      href: step.href,
      completed,
      state: resolveState(completed, isComingSoon, isFirstIncomplete),
    };
  });

  const countable = steps.filter((s) => s.kind !== 'coming_soon');
  const completedCount = countable.filter((s) => s.completed).length;
  const nextStep =
    steps.find((s) => s.state === OnboardingLadderStepState.CURRENT) ?? null;

  return {
    steps,
    nextStep,
    completedCount,
    totalCount: countable.length,
    isComplete: countable.length > 0 && completedCount === countable.length,
  };
}
